// filepath: /src/hooks/useAuth.js
import { useState, useEffect } from 'react';

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Cargar usuario desde localStorage al inicializar
  useEffect(() => {
    try {
      // Primero intentar cargar desde las claves de amplify (datos reales del API)
      const amplifyUser = localStorage.getItem('__amplify__user');
      const amplifyToken = localStorage.getItem('__amplify__token');
      const amplifyNombre = localStorage.getItem('__amplify__nombre');
      const amplifyInstitutions = localStorage.getItem('__amplify__institutions');
      
      if (amplifyUser && amplifyToken) {
        const userData = JSON.parse(amplifyUser);
        const institutions = amplifyInstitutions ? JSON.parse(amplifyInstitutions) : [];
        
        const userObject = {
          username: userData.vc_email,
          name: `${userData.vc_nombre} ${userData.vc_apellidoPaterno || ''} ${userData.vc_apellidoMaterno || ''}`.trim(),
          avatar: null,
          role: institutions?.[0]?.roles?.[0]?.vc_slug || 'user',
          id: userData.id,
          email: userData.vc_email,
          token: amplifyToken,
          userData,
          institutions
        };
        
        setUser(userObject);
        // También guardarlo en la clave temporal para compatibilidad
        localStorage.setItem('thinkhealth_user', JSON.stringify(userObject));
      } else {
        // Fallback: intentar cargar desde la clave temporal
        const storedUser = localStorage.getItem('thinkhealth_user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          setUser(userData);
        }
      }
    } catch (error) {
      console.error('Error loading user from localStorage:', error);
      // Limpiar datos corruptos
      localStorage.removeItem('thinkhealth_user');
      localStorage.removeItem('__amplify__user');
      localStorage.removeItem('__amplify__token');
      localStorage.removeItem('__amplify__nombre');
      localStorage.removeItem('__amplify__institution');
      localStorage.removeItem('__amplify__institutions');
    } finally {
      setIsLoading(false);
    }

    // Listener para detectar logout desde el sistema principal
    const handleLogoutMessage = (event) => {
      // Verificar que el mensaje viene del sistema principal
      const allowedOrigins = [
        'http://localhost:3000',
        'https://sistema.thinkhealth.com', // Cambiar por la URL real del sistema
        'https://app.thinkhealth.com',     // Cambiar por la URL real del sistema
        // Agregar más orígenes según sea necesario
      ];
      
      if (!allowedOrigins.includes(event.origin)) {
        return; // Ignorar mensajes de orígenes no permitidos
      }
      
      // Detectar mensaje de logout
      if (event.data && event.data.type === 'LOGOUT_SYNC') {
        console.log('🔄 Landing: Detectado logout desde sistema principal, limpiando datos...');
        logout(); // Limpiar datos de la landing page
      }
    };

    // Agregar listener para mensajes de logout
    window.addEventListener('message', handleLogoutMessage);

    // Cleanup: remover listener al desmontar
    return () => {
      window.removeEventListener('message', handleLogoutMessage);
    };
  }, []);

  // Función para hacer login
  const login = (userData) => {
    try {
      setUser(userData);
      localStorage.setItem('thinkhealth_user', JSON.stringify(userData));
    } catch (error) {
      console.error('Error saving user to localStorage:', error);
    }
  };

  // Función para hacer logout
  const logout = () => {
    try {
      setUser(null);
      // Limpiar TODAS las claves relacionadas con la autenticación
      const keysToClean = [
        'thinkhealth_user',
        'thinkhealth_last_login',
        'thinkhealth_last_login_secure', // Credenciales encriptadas para auto-login
        '__amplify__user',
        '__amplify__token',
        '__amplify__nombre',
        '__amplify__institution',
        '__amplify__institutions',
        '__amplify__roles',
        '__amplify__user_id',
        '__amplify__vc_email',
        '__amplify__vc_apellidoPaterno',
        '__amplify__vc_apellidoMaterno',
        '__amplify__rol'
      ];
      
      // Limpiar todas las claves específicas
      keysToClean.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Limpieza adicional: buscar y limpiar cualquier clave que contenga patrones relacionados
      try {
        const allKeys = [];
        for (let i = 0; i < localStorage.length; i++) {
          allKeys.push(localStorage.key(i));
        }
        
        allKeys.forEach(key => {
          if (key && (
            key.includes('amplify') || 
            key.includes('thinkhealth') || 
            key.includes('think_health') ||
            key.includes('user') ||
            key.includes('institution') ||
            key.includes('login')
          )) {
            localStorage.removeItem(key);
          }
        });
        
        console.log('🧹 Landing: localStorage limpiado completamente');
      } catch (error) {
        console.warn('⚠️ Error en limpieza adicional de localStorage:', error);
      }
      
    } catch (error) {
      console.error('Error removing user from localStorage:', error);
    }
  };

  // Función para actualizar datos del usuario
  const updateUser = (updatedData) => {
    try {
      const newUserData = { ...user, ...updatedData };
      setUser(newUserData);
      localStorage.setItem('thinkhealth_user', JSON.stringify(newUserData));
    } catch (error) {
      console.error('Error updating user in localStorage:', error);
    }
  };

  // Verificar si el usuario está autenticado
  const isAuthenticated = Boolean(user);

  return {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    updateUser
  };
};

export default useAuth;
